/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* model.ts
* 11-2-2021 - Jelmer Jellema - Spin in het Web B.V.
*
* Basemodel - general code for use in our datamodels
*
* The models aren't services. They are plain classes. We do use a static injector
* to add some services to basemodel. This initialized in app component
*
* Internal properties start with _, as to discern them from model fields.
*
* Models know how to get their data from the backend. Only implement specific backend calls through
* records(...) or cachedRecords(...) if the basic calls here aren't enough
*/

import {Injector} from '@angular/core';
import {hash} from '../app/types';
import {Sihwlog} from 'sihw-ngx';
import {ApiService, Datarecord, Datarecords, Recordquery} from '../services/api/api.service';
import {cloneDeep} from 'lodash-es';

/** modelfield decorator **/
export function ModelField(): any {
  return function(target: Basemodel, key: string, descriptor: PropertyDescriptor) {
    return (<typeof Basemodel> target.constructor)._modelFieldDecorator(key, descriptor);
  };
}

/**
 * Ownerfield decorator: adds a field that will be filled with the id of the owner, other side of a 1:n relation
 * @constructor
 */
export function OwnerField(): any {
  return function(target: Basemodel, key: string, descriptor: PropertyDescriptor) {
    return (<typeof Basemodel> target.constructor)._ownerFieldDecorator(key, descriptor);
  };
}

export interface modelrelation {
  type?: '1:n' | '1:1'; //type of relation. Default is 1:n. 1:1 betekent ook dat de FK in het kind zit, maar dat we dit afhandelen als max 1 per main
  model?: typeof Basemodel; //the related model
}

/**
 * Modelrelation decorator
 */
export function ModelRelation(definition: modelrelation = {}): any {
  return function(target: Basemodel, key: string, descriptor: PropertyDescriptor) {
    return (<typeof Basemodel> target.constructor)._modelRelationDecorator(key, descriptor, definition);
  };
}

export type modelrelations = hash<modelrelation>;

export class Basemodel {
  //static
  static table: string; //the table we are modelling
  static defaultOrder: (string | string[])[] = null; //sequalize-syntax for order when getting data through our static calls
  static loglevel: string = 'warn';
  static softdeletes: boolean = false; //heeft dit model softdeletes? Als dat zo is, dan halen we het softdeleted-veld stiekem ook op LET OP: de softdeleted vlag moet gewoon mee in de query-opties, anders komen ze niet van achteren
  private isSoftdeleted: boolean = false //is dit record soft deleted?

  //internal properties of a  model
  protected static fields: string[] = []; //the fields we can get from / save to the backend database Use the @Modelfield decorator to set this
  protected static ownerfield: string | null = null; //the name of the ownerfield set with @Ownerfield. This field maps on the id of this._owner
  protected static relations: modelrelations = {}; //relation definitions. Use @ModelRelation to set this
  private static basemodelInit: boolean = false; //has the app.component called Basemodel.init?
  private static services: hash<any> = {};
  private static log: Sihwlog = null; //see _log(...)

  /**
   * standard properties of the relation definition
   * @private
   */
  private static defaultRelationProps = {
    type: '1:n', //The foreign key is in the foreign table, pointing back to the model's table
    model: <Basemodel> null
  };


  /**
   * Original data. Must be set, one way or another: by the owner when getting the data for this sub model
   * automatically set by setData of the owner, so also in the static that get or create data
   * @protected
   */
  protected _orig: hash<any> = {}; //de originele data volgens __setData(..., true);

  /**
   * Generated critique. Can be used to structure checks in that data. See hergeneerKritiek()
   * @private
   */
  private _kritiek: hash<string[]> = null;

  _tags: hash<any> = {}; //Space to add random data to a model instance, can be used to save ux data

  /**
   * the owner. For example: a function can be owned by a person
   */
  public _owner: Basemodel = null;

  //the only field we always define, because of usage in code
  id: number | string;

  /**
   * Internal data for the trackedId getter
   * @private
   */
  private _trackerid: string;
  static trackerIdSerial: number = 0; //make sure every instance has it's unique id nuber
  /**
   * META STATIC
   * Initialise the class with the approot injector. Must be called in the app.component constructor
   * @param injector
   */
  static init(injector: Injector) {
    if (Basemodel.basemodelInit) {
      return; //al gedaan
    }
    Basemodel.services.injector = injector;
    //some services that we always load
    Basemodel.services.api = injector.get(ApiService);
    Basemodel.basemodelInit = true;
  }

  /**
   * Implements @ModelField decorator
   */
  public static _modelFieldDecorator<Modelclass extends typeof Basemodel>(this: Modelclass, key: string, descriptor: PropertyDescriptor) {
    //we just make a field (property) and save it the fields array that we defined at the level of specific classes

    if (!this.hasOwnProperty('fields')) {
      //copy the definition from basemodel
      Object.defineProperty(this, 'fields', Object.getOwnPropertyDescriptor(Basemodel, 'fields'));
      this.fields = [];
    }
    this.fields.push(key);
    descriptor = descriptor || {};
    descriptor.enumerable = true;
    descriptor.writable = true;
    return descriptor;
  }

  /**
   * Implements @OwnerField decorator: adds a field for the id of the _owner
   * @param key
   * @param descriptor
   */
  public static _ownerFieldDecorator<Modelclass extends typeof Basemodel>(this: Modelclass, key: string, descriptor: PropertyDescriptor) {
    if (!this.hasOwnProperty('ownerfield')) {
      //copy the definition from basemodel
      Object.defineProperty(this, 'ownerfield', Object.getOwnPropertyDescriptor(Basemodel, 'ownerfield'));
      this.ownerfield = null;
    }
    this.ownerfield = key; //so code can use it. It is NOT part of fields, but will be used in savedata etc
    descriptor = {
      get() {
        return (<InstanceType<Modelclass>> this)._owner?.id;
      },
      enumerable: true
    };
    return descriptor;
  }


  /**
   * Implements the @ModelRelation decorator
   */
  public static _modelRelationDecorator<Modelclass extends typeof Basemodel>(this: Modelclass, key: string, descriptor: PropertyDescriptor, definition: modelrelation) {
    //save the field and add it to the relations array of the specific Basemodel subclass

    if (!this.hasOwnProperty('relations')) {
      //copy from basemodel
      Object.defineProperty(this, 'relations', Object.getOwnPropertyDescriptor(Basemodel, 'relations'));
      this.relations = {};
    }
    this.relations[key] = definition;
    descriptor = descriptor || {};
    descriptor.enumerable = true;
    descriptor.writable = true;
    return descriptor;
  }

  /**
   * Statics for getting data and creating instances. Just using the constructor is not enough
   */

  /**
   * Create a new instance for use as a new record. Will set defaults and initialize the relations as empty arrays. Can initialize with given data.
   * @param owner Owner of the new instance
   * @param data Optional init data
   */
  static createNew<Modelclass extends typeof Basemodel>(this: Modelclass, owner: Basemodel = null, data: Datarecord | hash<any> = null): InstanceType<Modelclass> {
    let inst = new this as InstanceType<Modelclass>;
    //alle defaults are set now
    //we have to create the right relation structure
    for (let relkey of Object.keys(this.relations)) {
      inst[relkey] = inst.relationDetails(relkey).type === '1:n' ? [] : null;
    }

    //overwrite with given data
    if (data) {
      inst.setData(data, false); //don't do orig now, we do it below
    }

    //create orig: same data as the base
    inst._orig = {};
    for (let field of inst._fieldsAndRelations) {
      inst._orig[field] = this[field];
    }
    //owner (and ownerfield, if set)
    inst._owner = owner;
    return inst;
  }

  /////// Create an instance from given (backend) data
  static create<Modelclass extends typeof Basemodel>(this: Modelclass, data: Datarecord | hash<any> = {}, owner: Basemodel = null): InstanceType<Modelclass> {
    let inst = new this as InstanceType<Modelclass>;
    inst.setData(data, true);
    if (this.softdeletes && (!! data.deletedAt))
    {
      inst.isSoftdeleted = true;
    }
    //save the owner
    inst._owner = owner;
    return inst;
  }

  /**
   * Create multiple instance from given (backend) data
   * @param data
   * @param owner
   */
  static createAll<Modelclass extends typeof Basemodel>(this: Modelclass, data: Datarecords | hash<any>[], owner: Basemodel = null): InstanceType<Modelclass>[] {
    return data.map(rec => this.create(rec, owner));
  }

  ///////// from database

  /**
   * Create multiple instances by getting records from the backend through api.cachedRecords (so use caching)
   */
  static async fromQuery<Modelclass extends typeof Basemodel>(this: Modelclass, query: Recordquery, owner: Basemodel = null, apiblock: boolean = true, caching: boolean = true, errortoast: boolean = true): Promise<InstanceType<Modelclass>[]> {
    query = Object.assign({}, query);
    query.table = this.table;
    let data: Datarecords;
    if (caching)
    {
      data = await Basemodel.services.api.cachedRecords(query, true, apiblock, errortoast);
    }
    else {
      data = await Basemodel.services.api.records(query, apiblock, errortoast);
    }
    return this.createAll(data, owner);
  }

  /**
   * Basic call for gettig a list of model instances from api.cachedRecords. Combines fromQuery() and recordquery()
   * @param models When given, only relations for the models mentioned will be got. This can be recursive models. If not given, all subrecords will be queried, even recursively. If an empty array, no subrecords will be queried
   * @param suboptions  Options per model. An array of tubles model, options
   * @param owner the owner of the new top objects
   * @param aclModel If given: the model used for ACL check (the lookup argument in the query. see api)
   * @param throughOwner If given, this must be the model class of the owner. We will first try to get this one, and set it as owner of the new instances. This messes with the ourder of the records. And look out for ACL trouble
   * @param apiblock wel of geen block bij ophalen data
   * @param caching Wel of niet cachen van het resultaat (standaard = ja)
   * @param errortoast Wel of niet een toast weergeven als er iets mis gaat (standaard = ja)
   * @returns Promise<InstanceType<Modelclass>[]>
   */
  static async getAll<Modelclass extends typeof Basemodel>(this: Modelclass, models?: (typeof Basemodel)[], suboptions: [typeof Basemodel, hash<any>][] = [], owner: Basemodel = null, aclModel: typeof Basemodel = null, throughOwner: typeof Basemodel = null, apiblock: boolean = true, caching: boolean = true, errortoast: boolean = true): Promise<InstanceType<Modelclass>[]> {
    // console.log(`${this.name} getAll`,models);
    if (!throughOwner) {
      let q = this.recordquery(models, suboptions);
      if (aclModel) {
        q.lookup = aclModel.table;
      }
      return this.fromQuery(q, owner, apiblock, caching, errortoast);
    }
    //thoughowner.

    let owners = await throughOwner.getAll(models ? [this, ...models] : null, suboptions, null, aclModel, null, apiblock, caching, errortoast);
    //hoe heten wij in deze owner?
    let relkey = Object.keys(throughOwner.relations).find(relkey => throughOwner.relations[relkey].model === this); //kan anders zijn dan onze tablename
    if (relkey) {
      //concat de arrays uit elke owner
      return [].concat(...owners.map(owner => owner[relkey]));
    }
    else {
      this.classlog.warn(`getAll: geen relatie tussen gevraagde owner ${throughOwner.name} en dit model ${this.name}`);
      return [];
    }
  }

  /**
   * Create an instance from one backend record
   */
  static async fromId<Modelclass extends typeof Basemodel>(this: Modelclass, id: number, models?: (typeof Basemodel)[], suboptions: [typeof Basemodel, hash<any>][] = [], owner: Basemodel = null, force: boolean = false): Promise<InstanceType<Modelclass>> {
    let thisOptions = suboptions.find(su => su[0] === this);
    if (!thisOptions) {
      thisOptions = [this, {}];
      suboptions.push(thisOptions);
    }
    thisOptions[1].single = id;
    let data = await Basemodel.services.api.cachedRecords(this.recordquery(models, suboptions), true, true, true, force);
    return data ? this.create(data, owner) : null;
  }

  /**
   * Get a lookup version of the model: returns a hash with a key field value als id, and the model instance as value
   * @param aclModel model to be used for acl checks (lookup call)
   * @param models related models that are needed Default is none
   * @param suboptions subopties zoals bij getall
   */
  static async getLookup<Modelclass extends typeof Basemodel>(this: Modelclass, aclModel?: typeof Basemodel, models: (typeof Basemodel)[] = [], suboptions: [typeof Basemodel, hash<any>][] = []): Promise<hash<InstanceType<Modelclass>>> {
    if (aclModel) {
      //voeg de lookup toe aan de suboptions voor dit model
      const thismodeloptions = suboptions.find(so => so[0] === this);
      if (thismodeloptions)
      {
        thismodeloptions[1].lookup = aclModel.table;
      }
      else {
        suboptions.push([this, {lookup: aclModel.table}]);
      }
    }
    let res: hash<InstanceType<Modelclass>> = {};
    let instances = await this.getAll(models, suboptions);
    for (let inst of instances) {
      res[inst.id] = inst;
    }
    return res;
  }

  /**
   * create a valid record query.
   * @param models When given, only relations for the models mentioned will be got. This can be recursive models. If not given, all subrecords will be queried, even recursively. If an empty array, no subrecords will be queries
   * @param suboptions  Options per model. An array of tubles model, options
   */
  static recordquery(models?: (typeof Basemodel)[], suboptions?: [typeof Basemodel, hash<any>][]): Recordquery {
    // console.log(`${this.name} recordQuery`,models,this.relations);
    let fields = [...this.fields];
    if (this.softdeletes)
    {
      fields.push('deletedAt');
    }
    let q: Recordquery = {
      table: this.table,
      attributes: fields,
      connected: []
    };
    //sort?
    //do this before suboptions, so it can be overruled
    if (this.defaultOrder) {
      q.order = this.defaultOrder;
    }
    if (suboptions) {
      let modeloptions = suboptions.find(t => t[0] === this);
      if (modeloptions) {
        Object.assign(q, modeloptions[1]);
      }
    }

    for (let relkey of Object.keys(this.relations)) {
      let rel = this.relations[relkey];
      // console.log(`${this.name} ${relkey} ${rel.model.name}`);
      if ((!models) || models.includes(rel.model)) {
        //create recursive connection
        let connection = rel.model.recordquery(models, suboptions);
        q.connected.push(connection);
      }
    }
    return q;
  }

  /////////////////////////////////// Constructor ///////////////////////////////
  constructor() {
  }

  //////////////////////// logic ////////////////////////////
  //publics are at the end

  /**
   * Helper for findng and setting static properties in the actual class. These must be properties that are defind in Basemodel.
   * @protected
   */
  protected get _actualClass(): typeof Basemodel {
    return this.constructor as typeof Basemodel;
  }

  /**
   * Get the fiels from the class props
   * @protected
   */
  protected get _fields(): string[] {
    return this._actualClass.fields;
  }

  protected get _ownerfield(): string | null {
    return this._actualClass.ownerfield;
  }

  protected get _relations(): modelrelations {
    return this._actualClass.relations;
  }

  /**
   * De fields for relations
   * @protected
   */
  protected get _relationFields(): string[] {
    return Object.keys(this._actualClass.relations);
  }

  /**
   * Combineer all data properties
   * @protected
   */
  protected get _fieldsAndRelations(): string[] {
    return [...this._fields, ...this._relationFields];
  }

  /**
   * Get the details for a relation
   * @param relationKey
   * @protected
   */
  protected relationDetails(relationKey: string): modelrelation {
    //defaults erin
    return Object.assign({}, Basemodel.defaultRelationProps, this._actualClass.relations[relationKey]);

  }

  //////////////////////// Publics ////////////////////////////

  /**
   * Set the data, only accepting defined fields and relations
   * @param data
   * @param resetOrig when true, also reset the _orig for all fields
   */
  public setData(data: hash<any>, resetOrig: boolean = false) {
    //velden
    for (let field of this._fields) {
      if (field in data) {
        this[field] = data[field];
      }
    }
    //relations
    for (let relkey of this._relationFields) {
      let rel = this.relationDetails(relkey);
      let reldata: hash<any> | hash<any>[];
      if (rel.model && rel.model.table && (rel.model.table in data)) {
        reldata = data[rel.model.table];
      }
      if (!reldata) {
        //try relkey itself
        reldata = data[relkey];
      }
      //1:1 relatie? Dan de eerste uit een array
      if (rel.type === '1:1' && Array.isArray(reldata))
      {
        reldata = reldata[0]; //kan undefined zijn
      }
      if (reldata) {
        //not null or undefined

        // this._log.debug(`Relatie`, rel);

        //create a model instance?
        if (rel.model) {
          if (Array.isArray(reldata)) {
            reldata = reldata.map(rec =>
              (rec instanceof rel.model) ? rec : rel.model.create(rec, this));
          } else {
            reldata = rel.model.create(reldata, this);
          }
        } else {
          this._log.warn(`${this._actualClass.name}: Relations ${relkey} doesn't have a model. It will be used as plain data`);
        }
      }
      //add
      this[relkey] = reldata; //kan dus ook undefined
    }

    if (resetOrig) {
      this._orig = {};
      for (let field of this._fieldsAndRelations) {
        this._orig[field] = this[field];
      }
    }
    //reset the critique
    this.resetKritiek();
  }

  /**
   * Reset the record by getting the data once again from the backend
   * existing subrecords that are not queries will remain in the data. _orig will be reset.
   * @param models zie Basemodel.recordquery
   * @param suboptions zie Basemodel.recordquery
   * @param clearCacheBefore Als true, dan wordt de cache niet gebruikt, maar het resultaat wordt wel in de cache geschreven
   */
  public async resetFromDB(models?: (typeof Basemodel)[], suboptions?: [typeof Basemodel, hash<any>][], clearCacheBefore: boolean = false): Promise<void> {
    if (!this.id) {
      this._log.warn(`${this._actualClass.name}: resetFromDB zonder id`);
      return;
    }
    let q = this._actualClass.recordquery(models, suboptions);
    q.single = this.id; //fix hem op deze
    let data = await Basemodel.services.api.cachedRecords(q, true, true, true, clearCacheBefore);
    if (data) {
      this.setData(data, true);
    }
    this.resetKritiek();
  }

  /**
   * Return all fields with current data
   * No deepcopy
   */
  public get _data(): hash<any> {
    let res = {};
    for (let field of this._fieldsAndRelations) {
      res[field] = this[field];
    }
    if (this._ownerfield) {
      res[this._ownerfield] = this[this._ownerfield];
    }
    return res;
  }

  /**
   * Returns one _orig field or all _orig data
   * No deepcopy
   * @param key
   */
  public orig(key?: string): any {
    if (key) {
      if (this._fieldsAndRelations.includes(key)) {
        return this._orig[key];
      } else {
        return null;
      }
    }
    //everythinh:
    let res = {};
    for (let field of this._fieldsAndRelations) {
      res[field] = this._orig[field];
    }
    return res;
  }

  /**
   * Clones the model and it's related models as if it's a new model. Watch out:
   * - Fields will be copied (shallow), not cloned
   * - We do not set the owner of the top element, we do set it for sub model instances
   * - Orig will be the default values from the constructor
   */
  cloneAsNew<Modelinstance extends Basemodel>(this: Modelinstance): Modelinstance {
    let inst = new this._actualClass as Modelinstance;
    //bouw orig op
    inst._orig = {};
    for (let field of inst._fieldsAndRelations) {
      inst._orig[field] = inst[field];
    }

    for (let field of this._fields) {
      inst[field] = this[field];
    }
    //new: id is dus weg
    inst.id = null;
    //relaties
    for (let relkey of Object.keys(this._relations)) {
      let rel = this.relationDetails(relkey);
      if (Array.isArray(this[relkey])) {
        inst[relkey] = this[relkey].map(el => el.cloneAsNew());
        for (let el of inst[relkey]) {
          el._owner = inst;
        }
      } else if (this[relkey] instanceof rel.model) {
        inst[relkey] = this[relkey].cloneAsNew();
        inst[relkey]._owner = inst;
      } else {
        //copy, probably of undefined
        inst[relkey] = this[relkey];
      }
    }

    //isSoftdeleted laten we false bij een clone

    return inst;
  }

  /**
   * Create a full clone of the instance: data, orig and owner. Critique must be reset
   */
  fullClone<Modelinstance extends Basemodel>(this: Modelinstance): Modelinstance {
    let inst = new this._actualClass as Modelinstance;
    //copy orig
    inst._orig = {};
    for (let field of inst._fieldsAndRelations) {
      inst._orig[field] = this._orig[field];
    }

    for (let field of this._fields) {
      inst[field] = this[field]; //shallow!
    }

    inst._owner = this._owner;

    //relations
    for (let relkey of Object.keys(this._relations)) {
      let rel = this.relationDetails(relkey);
      if (Array.isArray(this[relkey])) {
        inst[relkey] = this[relkey].map(el => el.fullClone());
        for (let el of inst[relkey]) {
          el._owner = inst;
        }
      } else if (this[relkey] instanceof rel.model) {
        inst[relkey] = this[relkey].fullClone();
        inst[relkey]._owner = inst;
      } else {
        //shallow copy, probably of undefined
        inst[relkey] = this[relkey];
      }
    }

    //tags, these are just a deep clone
    inst['_tags'] = cloneDeep(this._tags);

    //reset critique
    inst.resetKritiek();

    return inst;
  }

  /**
   * Geef de datastructuur voor opslaan terug. Dit is gewoon de _data, plus de relaties via de _connected array
   */
  savedata(): Datarecord {
    let data = <Datarecord> this._data;
    for (let relkey of this._relationFields) {
      if ((relkey in data) && (data[relkey])) {
        let rel = this.relationDetails(relkey);
        if (rel.model) {
          data._connected = data._connected || [];
          //maak de connected info,inclusief de data
          //dit gaat recursief via savedata() als het een model is
          let reldata = data[relkey];
          if (rel.type === '1:1' && (! Array.isArray(reldata)))
          {
            //bij speciaal type 1:1 is het achterin een array
            reldata = [reldata];
          }
          data._connected.push({
            table: rel.model.table,
            fullset: true,
            data: Array.isArray(reldata) ?
              reldata.map(r => (r instanceof rel.model) ? r.savedata() : r)
              :
              (reldata instanceof rel.model) ? reldata.savedata() : data[relkey]
          });
        } else {
          this._log.warn(`${this._actualClass.name}: Relatie ${relkey} heeft geen model: kan niet worden meegenomen in savedata, want we weten niet welke
           tabel`);
        }
        delete data[relkey];
      }
    }
    //dit gebeurt vast omdat er iets gewijzigd is
    //dus resetten we voor de zekerheid de kritiek
    this.resetKritiek();
    return data;
  }

  /**
   * Nu opslaan
   */
  public async save(): Promise<void> {
    if (this.isSoftdeleted)
    {
      throw new Error(`${this.constructor.name}: Softdeleted`);
    }
    const res = await (<ApiService> Basemodel.services.api).save({
      table: this.table,
      data: this.savedata()
    });
    if (Number.isInteger(res.saved))
    {
      this.id = res.saved;
    }
  }

  /**
   * Zet een softdeleted item terug
   */
  public async restore(): Promise<void> {
    if (this.isSoftdeleted && (! this.isNieuw()))
    {
      //we gaan het opslaan
      await(<ApiService> Basemodel.services.api).restore(this.table,this.id);
      this.isSoftdeleted = false;
    }

  }
  ///////// helpers ///////////////
  /**
   * Is dit een nieuw, niet opgeslagen record?
   */
  public isNieuw(): boolean {
    return !this.id;
  }

  public get isDeleted(): boolean {
    return this.isSoftdeleted;
  }

  public get table(): string {
    return this._actualClass.table;
  }

  /**
   * Geef de velddata terug zonder relaties of ingevuld ownerfield
   */
  public get fieldData(): hash<any> {
    let res = {};
    for(let field of this._fields)
    {
      res[field] = this[field];
    }

    return res;
  }

  /**
   * Geeft een vaste tracker-id voor in ngFor of names van controls (gebruik GEEN index uit ngFor for names,dat gaat mis bij splice etc)
   */
  public get trackerId(): string {
    if (!this._trackerid) {
      this._trackerid = `${Basemodel.trackerIdSerial++}`; //goed genoeg, vind ik
    }
    return this._trackerid;
  }

  //on demand logger
  //gebruikt de classlogger, als die er is
  public get _log(): Sihwlog {
    return this._actualClass.classlog; //eventueel aangemaakt
  }

  private static get classlog(): Sihwlog {
    if (! this.log)
    {
      this.log = new Sihwlog(this.loglevel, this.name);
    }
    return this.log;
}

protected static get api(): ApiService {
    return this.services.api;
}

  protected get _api(): ApiService {
    return Basemodel.services.api;
  }


  /**
   * Semi-abstract. Te gebruiken voor titles etc
   */
  public get recordbeschrijving(): string {
    return '';
  }

  /********************** Kritiek *********************
   * Kritiek zijn zaken die mis zijn met een instance
   * Het wordt één keer gegenereerd wanneer er om gevraagd wordt
   * Daarna wordt het bewaard, tenzij expliciet resetKritiek()
   * wordt aangeroepen. Dat moet dus bij het wijzigen van een
   * model. Sommige calls die data wijzigen of gewijzigde data
   * veronderstellen doen dat zelf al.
   *
   * Geen van de kritiek is async, data moet dus al aanwezig zijn
   * als een model gerelateerde data mist, kan hij er ook geen
   * kritiek over geven.
   *
   * Basisgetter: kritiekPerCat. Geeft een hash van categorieën
   * met per categorie een array met zinnen (taal) terug.
   * Het specifieke model bepaalt welke groepen er zijn.
   * Catgorieën zijn er alleen als de array niet leeg is,
   * dus je kan mijnmodel.kritiekPerCat.categorie checken.
   *
   * Helpers:
   * get kritiek: samengevoegde kritiek zonder categorieën
   * get heeftKritiek: boolean. Snelle check op kritiek
   *
   * Maken kritiek per model: overrule de functie
   * maakKritiek: die moet een hash met categorieën en stringarrays
   * opleveren. Deze wordt aangeroepen door hergenereerKritiek
   * Super hoeft niet aangeroepen te worden, kan wel.
   * Mag lege categorieën teruggeven, die worden er wel uitgehaald.
   */

  /**
   * Kritiekgenerator. Vult this._kritiek door maakKritiek aan te roepen
   * @private
   */
  private hergenereerKritiek() {
    let kritiek = this.maakKritiek();
    this._kritiek = {};
    //even filteren
    for (let cat of Object.keys(kritiek)) {
      if (kritiek[cat] && kritiek[cat].length) {
        this._kritiek[cat] = kritiek[cat];
      }
    }
  }

  /**
   * Zorgt dat de kritiek (wanneer nodig) opnieuw gemaakt wordt
   */
  public resetKritiek() {
    this._kritiek = null; //dus de getters gaan hergenereerKritiek aanroepen
  }

  /**
   * Te overschrijven kritiekgenerator. Deze moet ingevuld worden en
   * de juiste return geven. Super hoeft niet aangeroepen te worden
   * maar kan wel natuurlijk. Mag lege categorieën teruggeven, die worden
   * door hergenereerKritiek gefilterd
   * @protected
   */
  protected maakKritiek(): hash<string[]> {
    return {}; //standaard
  }

  /**
   * model.kritiekPerCat.<categorie> geeft een array met problemen of undefined als er geen problemen zijn. Per model gedefinieerd.
   */
  public get kritiekPerCat(): hash<string []> {
    if (!this._kritiek) {
      //maken
      this.hergenereerKritiek();
    }
    return cloneDeep(this._kritiek);
  }

  /**
   * Levert de kritiek per regel op met steeds een array [catergoei, regel]
   */
  public get kritiekMetCat(): [string,string][] {
    if (!this._kritiek) {
      //maken
      this.hergenereerKritiek();
    }
    const res: [string, string][] = [];
    for(let cat of Object.keys(this._kritiek))
    {
      for(let k of this._kritiek[cat])
      {
        res.push([cat,k]);
      }
    }
    return res;
  }

  /**
   * Samenvatting van de kritiek zonder categorieën
   */
  public get kritiek(): string[] {
    if (!this._kritiek) {
      //maken
      this.hergenereerKritiek();
    }
    return [].concat(...Object.keys(this._kritiek).map(cat => this._kritiek[cat]));
  }


  /**
   * True als er kritiek is
   */
  public get heeftKritiek(): boolean {
    if (!this._kritiek) {
      //maken
      this.hergenereerKritiek();
    }
    return !!Object.keys(this._kritiek).length;
  }

  /**
   * Geef de kritiek als een string, voor in titles enzo
   */
  public get kritiekTekst(): string {
    return this.kritiek.join('\n');
  }

  /**
   * Als .kritiekTekst maar dan voor een specifieke categorie
   * @param cat
   */
  public kritiekTekstCat(cat: string): string {
    return (this.kritiekPerCat[cat] ?? []).join('\n');
  }
}
